import { StateCreator } from "zustand";

import { reorderColumns } from "ds/components/Table/Head/helpers";

import { type TableColumnsState } from "../types";

const makeTableColumnsSlice =
  <T extends TableColumnsState>(
    config: TableColumnsState["columnsState"]
  ): StateCreator<T, [["zustand/immer", never]], [["zustand/persist", never]], TableColumnsState> =>
  (set) => {
    const initialColumnsState = config;
    const initialColumnsSizes = Object.fromEntries(
      Object.entries(config).map(([key, value]) => [key, value.defaultWidth])
    );
    const initialColumnsOrder = {
      visible: Object.keys(config),
      hidden: [],
    };

    const initialColumnPinned = true;

    return {
      columnsState: initialColumnsState,
      columnsSizes: initialColumnsSizes,
      setColumnSize: (columnId, size) => {
        set((state) => {
          state.columnsSizes[columnId] = size;
        });
      },
      columnsOrder: initialColumnsOrder,
      reorderVisibleColumns: (columnId, direction) => {
        set((state) => {
          state.columnsOrder.visible = reorderColumns(
            state.columnsOrder.visible,
            columnId,
            direction
          );
        });
      },
      hideColumn: (columnId) => {
        set((state) => {
          state.columnsOrder = {
            visible: state.columnsOrder.visible.filter((id) => id !== columnId),
            hidden: state.columnsOrder.hidden.includes(columnId)
              ? state.columnsOrder.hidden
              : [...state.columnsOrder.hidden, columnId],
          };
        });
      },
      setColumnsOrder: ({ visible, hidden }) => {
        set((state) => {
          state.columnsOrder = {
            visible,
            hidden,
          };
        });
      },
      resetColumnsConfig: () => {
        set((state) => {
          state.columnsState = initialColumnsState;
          state.columnsSizes = initialColumnsSizes;
          state.columnsOrder = initialColumnsOrder;
          state.isFirstColumnPinned = initialColumnPinned;
        });
      },
      isFirstColumnPinned: initialColumnPinned,
      pinColumn: (columnId) => {
        set((state) => {
          const currentIndex = state.columnsOrder.visible.indexOf(columnId);

          if (currentIndex === 0) {
            // If it's already the first column, toggle sticky behavior
            state.isFirstColumnPinned = !state.isFirstColumnPinned;
          } else if (currentIndex > 0) {
            // Move column to first position and make it sticky
            const newOrder = [...state.columnsOrder.visible];
            const [columnToPin] = newOrder.splice(currentIndex, 1);
            newOrder.unshift(columnToPin);
            state.columnsOrder.visible = newOrder;
            state.isFirstColumnPinned = true;
          }
        });
      },
    };
  };

export default makeTableColumnsSlice;
